import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable } from 'mobx'
import { BulkActionStore } from '@App/stores/search/bulk-action-store'
import { WorkModel } from '@Entities/work/work'
import { createWorkMergeRequest } from '@App/api/routes/rightsholder/rightsholder'
import { WorkMergeRequest } from '@Entities/rightsholder/work-merge'

export enum WorkMergeBulkActionType {
    WorkMerge = 'WORK_MERGE',
}

export enum WorkMergeSteps {
    PrimarySelection = 'PRIMARY_SELECTION',
    RequestReview = 'REQUEST_REVIEW',
}

export const bulkActionLabels: Record<WorkMergeBulkActionType, string> = {
    [WorkMergeBulkActionType.WorkMerge]: 'Report Duplicates',
}

export interface WorkMergeGroup {
    id: number
    primaryWorkId: number
    worklist: Map<number, WorkModel>
    reason: string
}

const MAX_GROUP_COUNT = 10

export const getGroupWorks = (group: WorkMergeGroup) => ({
    primaryWork: group.worklist.get(group.primaryWorkId),
    secondaryWorks: Array.from(group.worklist.values()).filter((work) => work.id !== group.primaryWorkId),
})

export default class WorkMergeBulkActionStore extends BulkActionStore<WorkMergeBulkActionType, WorkMergeSteps> {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        super([WorkMergeSteps.PrimarySelection, WorkMergeSteps.RequestReview])
        this.rootStore = rootStore
        makeObservable<
            WorkMergeBulkActionStore,
            '_isMergingWorks' | '_mergeWorklist' | '_primaryWorkMergeId' | '_confirmedMergeGroups' | '_selectedWorks'
        >(this, {
            _isMergingWorks: observable,
            _mergeWorklist: observable,
            _primaryWorkMergeId: observable,
            _confirmedMergeGroups: observable,
            _selectedWorks: observable,
            setIsMergingWorks: action,
            setMergeWorklist: action,
            startMergeFromSelection: action,
            setPrimaryWorkMergeId: action,
            confirmCurrentGroup: action,
            removeConfirmedGroup: action,
            startNewGroup: action,
            syncSelectedWorks: action,
        })
    }

    private _isMergingWorks = false

    private _mergeWorklist: Map<number, WorkModel> = new Map()

    private _primaryWorkMergeId: number | undefined

    private _confirmedMergeGroups: WorkMergeGroup[] = []

    private _selectedWorks: Map<number, WorkModel> = new Map()

    private _nextGroupId = 1

    private buildGroupParams(group: WorkMergeGroup): WorkMergeRequest {
        const { primaryWork, secondaryWorks } = getGroupWorks(group)

        return {
            primaryPropertyId: group.primaryWorkId,
            primarySongCode: primaryWork?.songCode,
            reason: group.reason,
            secondaryWorks: secondaryWorks.map((work) => ({ propertyId: work.id as number, songCode: work.songCode })),
        }
    }

    async submitAllMergeRequests() {
        const groupParams = this._confirmedMergeGroups.map((group) => this.buildGroupParams(group))

        await createWorkMergeRequest(this.rootStore.rightsholderStore.currentRightsholderId, groupParams)
    }

    get isMergingWorks(): boolean {
        return this._isMergingWorks
    }

    get isReviewingRequest(): boolean {
        return this._isMergingWorks && this.currentStep === WorkMergeSteps.RequestReview
    }

    get mergeWorklist(): Map<number, WorkModel> {
        return this._mergeWorklist
    }

    get primaryWorkMergeId(): number | undefined {
        return this._primaryWorkMergeId
    }

    get confirmedMergeGroups(): WorkMergeGroup[] {
        return this._confirmedMergeGroups
    }

    get selectedWorks(): Map<number, WorkModel> {
        return this._selectedWorks
    }

    get isGroupCapReached(): boolean {
        return this._confirmedMergeGroups.length >= MAX_GROUP_COUNT
    }

    setIsMergingWorks(isMergingWorks: boolean): void {
        this._isMergingWorks = isMergingWorks
    }

    setMergeWorklist(worklist: Map<number, WorkModel>): void {
        this._mergeWorklist = worklist
    }

    startMergeFromSelection(): void {
        this._mergeWorklist = new Map(this._selectedWorks)
        this._isMergingWorks = true
    }

    syncSelectedWorks(worksOnPage: WorkModel[]): void {
        worksOnPage.forEach((work) => {
            if (work.id != null && this.idsSelectedForBulkAction.has(work.id)) {
                this._selectedWorks.set(work.id, work)
            }
        })

        this._selectedWorks.forEach((_, workId) => {
            if (!this.idsSelectedForBulkAction.has(workId)) {
                this._selectedWorks.delete(workId)
            }
        })
    }

    setPrimaryWorkMergeId(primaryWorkId: number): void {
        this._primaryWorkMergeId = primaryWorkId
    }

    confirmCurrentGroup(reason: string): void {
        if (this.isGroupCapReached || this._primaryWorkMergeId == null) {
            return
        }

        this._confirmedMergeGroups.push({
            id: this._nextGroupId++,
            primaryWorkId: this._primaryWorkMergeId,
            worklist: new Map(this._mergeWorklist),
            reason,
        })
        this.nextStep()
    }

    removeConfirmedGroup(id: number): void {
        this._confirmedMergeGroups = this._confirmedMergeGroups.filter((group) => group.id !== id)
    }

    startNewGroup(): void {
        this._mergeWorklist = new Map()
        this._primaryWorkMergeId = undefined
        this.resetSelection()
        this.setStep(WorkMergeSteps.PrimarySelection)
        this.setIsMergingWorks(false)
    }

    resetStore(): void {
        this.setActiveBulkAction(null)
        this._isMergingWorks = false
        this._mergeWorklist = new Map()
        this._confirmedMergeGroups = []
        this._selectedWorks = new Map()
        this.resetStep()
    }
}
